import { Button } from '@florencelabs/button';
import clsx from 'clsx';
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { NearestAvailability } from '@provider-directory/components/availability/availability.model';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import type { ComponentVariant } from '@provider-directory/components/select-appointment-slot-form/types';
import { getAvailablePhoneNumber } from '@provider-directory/functions/get-available-phone';
import type {
  ProviderResource,
  ProviderLocationResource,
} from '@provider-directory/types/providers.contracts';
import { formatPhone } from '../phone-number';

interface NoOnlineAvailabilityAtLocationProps {
  provider: ProviderResource | null;
  location: ProviderLocationResource | null;
  nearestAvailability: NearestAvailability | null;
  onViewNextAvailable: (locationId: string) => void;
  variant?: ComponentVariant;
}

export const NoOnlineAvailabilityAtLocation = ({
  provider,
  location,
  nearestAvailability,
  onViewNextAvailable,
  variant = 'system',
}: NoOnlineAvailabilityAtLocationProps): ReactElement => {
  const { t } = useTranslation();
  const orgContext = useOrgContext();

  const phone = getAvailablePhoneNumber(
    location,
    provider,
    orgContext.getAssociationPhoneSelection(),
  );

  const nextAvailableLocationId = nearestAvailability?.closestSlot?.locationId;

  const hasNextAvailable =
    nextAvailableLocationId &&
    nextAvailableLocationId !== location?.ehrExternalId;

  return (
    <div
      data-testid={'no-online-at-location'}
      className={clsx(
        'flex flex-col gap-4 rounded-sm p-4 text-body-small',
        variant === 'tonal' ? 'bg-primary-200' : 'bg-black/5',
      )}
    >
      <p>
        {phone
          ? t('availability.noOnlineAtLocation', { phone: formatPhone(phone) })
          : t('availability.noOnlineAtLocationNoPhone')}
      </p>
      {hasNextAvailable && (
        <Button
          testId={'view-next-available-location-btn'}
          variant={'systemLink'}
          size={'small'}
          icon={'arrow_forward'}
          iconPosition={'right'}
          onPress={() => {
            onViewNextAvailable(nextAvailableLocationId);
          }}
        >
          {t('availability.viewNextAvailableLocation')}
        </Button>
      )}
    </div>
  );
};
